"use client";
import { FlexBetween, FlexCol } from "@/components";
import { cn } from "@/lib";
import CustomButton from "../ui/customButton";
import {
  IInquiriesComponentProps,
  IUserRoleBase,
  TBouncedCheckInquiryInfer,
} from "@/interfaces";
import { zodResolver } from "@hookform/resolvers/zod";
import { bouncedCheckInquirySchema } from "@/validations";
import TextField from "../ui/textField";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import { FlexBetweenCol } from "../ui";
import { useBouncedCheckInquiryMutation } from "@/hooks/useInquiry";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { useModalStore } from "@/store";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { landingQuickAccessNavigation } from "@/constants";
import { useToastStore } from "@/store/toastStore";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { globalRawServicesApiUnification } from "@/constants/globalRawServicesData/globalRawServicesApiUnification";
import { IBouncedCheckInquiryMutationPayload } from "@/interfaces/inquiries/bank";

const BouncedCheckInquiry = ({
  openPaymentModal,
  isLoggedIn,
  navigateToLogin,
  isOpen,
  onClose,
  packages,
}: IInquiriesComponentProps) => {
  const t = useTranslations();
  const { closeModal } = useModalStore();
  const toggleRoleObj: {
    label: string;
    role: IUserRoleBase;
    isSelected: boolean;
  }[] = [
    {
      label: t("inquiries.bouncedCheck.role.individual"),
      role: "1",
      isSelected: true,
    },
    {
      label: t("inquiries.bouncedCheck.role.corporate"),
      role: "2",
      isSelected: false,
    },
  ];

  const { data: session } = useSession();
  const router = useRouter();
  const { setOpenToast } = useToastStore();
  const [toggleUserRole, setToggleUserRole] = useState(toggleRoleObj);
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors: formError },
  } = useForm<TBouncedCheckInquiryInfer>({
    resolver: zodResolver(bouncedCheckInquirySchema),
    defaultValues: {
      nationalCode: "",
      role: "1",
    },
  });
  const {
    isPending: isSubmittingBouncedCheck,
    mutate: bouncedCheckInquiryFn,
    error: bouncedCheckInquiryError,
    reset: resetBouncedCheckInquiry,
  } = useBouncedCheckInquiryMutation(session?.user.token);
  const [packageId, setPackageId] = useState<string | null>(null);
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const onSubmit = (data: TBouncedCheckInquiryInfer) => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };
  const bouncedCheckInquiryDetails = landingQuickAccessNavigation.find(
    (item) => item.key === globalRawServicesData.Bounced_Check.key,
  );

  const selectedRole: IUserRoleBase =
    toggleUserRole.find((role) => role.isSelected)?.role || "1";
  const paymentSubmission = () => {
    const formData = getValues();

    if (!formData.nationalCode) {
      setOpenToast({
        message: t("validation.nationalCode"),
        variant: "error",
      });
      return;
    }

    const payload: Readonly<IBouncedCheckInquiryMutationPayload> = {
      national_code: formData.nationalCode,
      customer_type: "1",
      campaign: packageId || undefined,
    };
    bouncedCheckInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
        },
      },
    );
  };

  return (
    <>
      <FlexBetween className="flex-col items-stretch gap-8 md:flex-row lg:gap-12">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn("flex-center mx-auto basis-full sm:basis-1/2")}
          style={{ maxWidth: "350px" }}
        >
          <FlexBetweenCol gap={6} className={cn("w-full xl:gap-8")}>
            <p className={cn("text-primary-main text-sm xl:text-base")}>
              کدملی مالک چک را در کادر زیر وارد نمایید.
            </p>
            {/*<FlexBetween gap={6} className="w-full">*/}
            {/*  {toggleUserRole.map(({ label, isSelected, role }, idx) => (*/}
            {/*    <ToggleCard*/}
            {/*      key={idx}*/}
            {/*      className={cn(*/}
            {/*        "hover:text-secondary-main h-10 grow border-gray-300 bg-white px-4 py-0 transition-colors xl:h-12",*/}
            {/*        isSelected && "border-transparent",*/}
            {/*      )}*/}
            {/*      isSelected={isSelected}*/}
            {/*      nodeEnd={<CheckBox isChecked={isSelected} />}*/}
            {/*      setSelected={() =>*/}
            {/*        setToggleUserRole((prev) =>*/}
            {/*          prev.map((item) =>*/}
            {/*            item.role === role*/}
            {/*              ? { ...item, isSelected: true }*/}
            {/*              : { ...item, isSelected: false },*/}
            {/*          ),*/}
            {/*        )*/}
            {/*      }*/}
            {/*    >*/}
            {/*      <p>{label}</p>*/}
            {/*    </ToggleCard>*/}
            {/*  ))}*/}
            {/*</FlexBetween>*/}
            <Controller
              name="nationalCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  maxLength={10}
                  numeric
                  hasShadow
                  inputMode="numeric"
                  aria-label="nationalCode"
                  placeholder={
                    selectedRole === "1"
                      ? t("general.nationalCode")
                      : t("general.nationalId")
                  }
                  inputClassName={cn("tracking-widest h-10")}
                  error={formError.nationalCode?.message}
                />
              )}
            />
            <CustomButton
              id="landing-bounced-check-submit"
              type="submit"
              className="mt-2 w-max lg:mt-4"
              onClick={handleSubmit(onSubmit)}
            >
              {t("inquiries.bouncedCheck.submitButton")}
            </CustomButton>
          </FlexBetweenCol>
        </form>
        <FlexCol
          gap={2}
          className={cn("hidden basis-1/2 sm:flex lg:gap-4 xl:gap-6")}
        >
          <h2 className={cn("text-sm font-bold lg:text-base xl:text-xl")}>
            {t("inquiries.bouncedCheck.headerTitle")}
          </h2>
          <p
            className={cn(
              "text-custom-gray-200 text-justify text-xs leading-5 whitespace-pre-line lg:text-sm lg:leading-normal xl:text-base",
            )}
          >
            {t("inquiries.bouncedCheck.description")}
          </p>
        </FlexCol>
      </FlexBetween>
      {isOpen && (
        <PaymentModalContainer
          payBtnId="landing-payment-bounced-check"
          submissionLoading={isSubmittingBouncedCheck}
          submissionError={bouncedCheckInquiryError}
          paymentSubmission={paymentSubmission}
          isOpen={isOpen}
          onClose={onClose}
          selectedService={bouncedCheckInquiryDetails}
          onErrorReset={resetBouncedCheckInquiry}
          packages={packages}
          showData="service"
          serviceId={globalRawServicesApiUnification.Bounced_Check.apiId}
          onPackageSelect={handlePackageSelect}
        />
      )}
    </>
  );
};

export default BouncedCheckInquiry;
